<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-alert
        variant="danger"
        :show="alertErrors.length > 0"
        class="mb-0"
      >
        <div
          v-for="(row, index) in alertErrors"
          :key="index"
          class="alert-body"
        >
          <feather-icon

            icon="InfoIcon"
            class="mr-50"
          />
          {{ row }}
        </div>
      </b-alert>
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>

              <!-- Field: Username -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Username"
                  :rules="`required|alpha-num|unique_username:${userData.id}`"
                >
                  <b-form-group

                    label="Username"
                    label-for="username"
                  >
                    <b-form-input
                      id="username"
                      v-model="useUserData.username"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    label="Name"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="useUserData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  :rules="`required|email|unique_email:${userData.id}`"
                >
                  <b-form-group
                    label="Email"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="useUserData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    label="Phone"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="useUserData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Address -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Address"
                  rules="required"
                >
                  <b-form-group
                    label="Address"
                    label-for="address"
                  >
                    <b-form-input
                      id="address"
                      v-model="useUserData.address"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Status -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  label="Login"
                  label-for="login-status"
                >
                  <v-select
                    v-model="useUserData.loginStatus"
                    :v-if="!loading"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="loginOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="login-status"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Role -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  label="User Role"
                  label-for="user-role"
                >
                  <v-select
                    v-model="role"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="roleOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="user-role"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: API token Permission -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_API_TOKEN_REVOKE')"
                  label-for="api-tokens-revoke"
                >
                  <v-select
                    v-model="useUserData.apiTokenRevoke"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="apiTokenRevokeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="api-tokens-revoke"
                    :value="1"
                  />
                </b-form-group>
              </b-col>

            </b-row>

            <!-- PERMISSION TABLE -->
            <b-card
              no-body
              class="border mt-1"
            >
              <b-card-header class="p-1">
                <b-card-title class="font-medium-2">
                  <feather-icon
                    icon="LockIcon"
                    size="18"
                  />
                  <span class="align-middle ml-50">Permission</span>
                </b-card-title>
              </b-card-header>
              <b-table
                v-if="!loader"
                striped
                responsive
                class="mb-0"
                :items="permissionsData"
              >
                <template #cell(module)="data">
                  {{ data.value }}
                </template>
                <template #cell()="data">
                  <b-form-checkbox
                    v-model="useUserData.permissions"
                    :checked="data.value"
                    :value="data.value"
                  />
                </template>
              </b-table>
            </b-card>
            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              Save Changes
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              Reset
            </b-button>
          </b-form>

        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput, BForm, BTable, BCard, BCardHeader, BCardTitle, BFormCheckbox,
  BFormInvalidFeedback, BOverlay, BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import { ref/* , onUnmounted  */ } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'

import formValidation from '@core/comp-functions/forms/form-validation'
import { required, alphaNum, email } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import userStoreModule from '../userStoreModule'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BTable,
    BCard,
    BCardHeader,
    BCardTitle,
    BFormCheckbox,
    vSelect,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    BOverlay,
    BAlert,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      selected: [],
      alertErrors: [],
      useUserData: { ...this.userData },
    }
  },
  setup(props) {
    const roleOptions = []

    const loginOptions = []
    const loading = true
    const loader = true
    const headingArray = []

    const permissionsData = []
    const refInputEl = ref(null)
    const previewEl = ref(null)
    const permissions = []
    const loginOptionsArgs = []

    const requestArgs = [
      { request: '/users/get-login-options', param: 'loginOptions' },
      { request: '/users/get-user-roles', param: 'roleOptions' },
    ]

    const apiTokenRevokeOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]

    const { role } = props.userData

    const {
      refFormObserver,
      getValidationState,
    } = formValidation(props.userData)

    return {
      avatarText,
      roleOptions,
      loginOptions,
      permissionsData,
      loading,
      loader,
      refInputEl,
      previewEl,
      headingArray,
      // inputImageRenderer,
      permissions,
      loginOptionsArgs,
      requestArgs,
      refFormObserver,
      getValidationState,
      BFormInvalidFeedback,
      role,
      apiTokenRevokeOptions,
    }
  },
  watch: {
    userData: {
      deep: true,
      handler(newValue) {
        console.log('Selected users changed', newValue.permissions)
      },
    },
    role: {
      handler() {
        this.getRolePermissions()
      },
    },
  },
  created() {
    this.getUserMultipleOptions()
    this.getPermissions()
  },
  methods: {
    onSubmit() {
      this.loader = true
      const USER_APP_STORE_MODULE_NAME = 'app-user'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)
      const permissions = []
      this.headingArray.forEach(heading => {
        // console.log('heading', heading)
        const result = this.useUserData.permissions.filter(word => word.includes(heading))
        const assignedPermissions = []
        result.forEach(item => {
          const ret = item.replace(`${heading}_`, '')
          assignedPermissions.push(ret)
        })
        permissions.push({ assignedPermissions, heading })
      })
      this.useUserData.role = this.role
      const data = { ...this.useUserData, permissions }
      // console.log('--->', data)
      store.dispatch('app-user/editUser', data)
        .then(() => {
          // console.log('done', response)
          this.$router.replace('/users/list')
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'User Updated',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'User successfully Updated!',
                },
              })
            })
        })
        .catch(error => {
          this.alertErrors = error.data.message
          // console.log('error**', this.alertErrors)
          this.loader = false
        })
    },
    userIsSelected(checkBox) {
      return this.selected.includes(checkBox)
    },
    async getPermissions() {
      const newArr = []
      axios
        .get('/role-management/default-permissions')
        .then(response => {
          const headingData = Object.keys(response.data.data)
          this.headingArray = headingData
          let newObj = null
          headingData.forEach(element => {
            newObj = response.data.data[element]
            delete newObj.model
            newArr.push(newObj)
          })
          this.permissionsData = newArr
          this.loader = false
          // console.log('new_Arr', this.permissionsData)
        })
        .catch(error => console.log('ERROR', error))
    },

    async getUserMultipleOptions() {
      try {
        await Promise.all(
          this.requestArgs
            .map(REQUEST => axios
              .get(REQUEST.request)
              .then(res => {
                if (REQUEST.param === 'loginOptions') {
                  this.loginOptions = [...res.data.data]
                }
                if (REQUEST.param === 'roleOptions') {
                  this.roleOptions = [...res.data.data]
                }
              })),
        )
          .then(() => {
            this.loading = false
          })
      } catch (error) {
        console.log('options error', error)
      }
    },
    resetForm() {
      this.useUserData = { ...this.userData }
    },
    async getRolePermissions() {
      this.selected = []
      const permissions = await axios.get('role-management/get-role-abilities', { params: { role: this.role } })
      if (permissions.status === 200) {
        /* const res = this.useUserData.permissions.filter(it => permissions.data.data.indexOf(it) < 0,
          permissions.data.data) */
        this.useUserData.permissions = [...permissions.data.data]
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.spinner-border{
  width: 6rem;
  height: 6rem;
}
</style>
